import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { UploadProgress } from 'src/context/UploadWidgetContext';
import { useUploadWidget } from 'src/context/UploadWidgetContext';
import { useServerStateCheck } from 'src/hooks/UploadManager/useServerStateCheck';
import { useUploadDocuments } from 'src/hooks/UploadManager/useUploadDocuments';
import { useUploadSession } from 'src/hooks/UploadManager/useUploadSession';
import useMutation from 'src/hooks/useMutation';
import {
  dataLibraryApi,
  useUploadDocumentsMutation
} from 'src/newServices/dataLibrary';
import { usePatchUploadSessionStatusMutation } from 'src/newServices/uploadSessions';
import { useDispatch } from 'src/redux/store';
import {
  ACCOUNT_TYPE,
  FILE_UPLOAD_ERROR_MESSAGES,
  FILE_UPLOADING_STATUS,
  UPLOAD_TYPE
} from 'src/types/enum';
import type { UploadFile } from 'src/utils/constants';
import { ALLOWED_DATA_LIBRARY_EXTENSIONS } from 'src/utils/constants';
import { ErrorContext } from 'src/utils/errorMappings';
import type { RejectedFile } from 'src/utils/files/fileSizeLimits';
import {
  FILE_REJECTION_REASON,
  validateUploadFiles
} from 'src/utils/files/fileSizeLimits';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { v4 as uuid } from 'uuid';

// Target ~1 animation frame of work per slice so the progress bar/text updates
// smoothly regardless of how expensive each file turns out to be.
const FRAME_BUDGET_MS = 12;

const yieldToMain = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

function computeProgress(files: UploadFile[]): UploadProgress {
  const successCount = files.filter(
    (f) => f.status === FILE_UPLOADING_STATUS.UPLOADED
  ).length;
  const failedCount = files.filter(
    (f) => f.status === FILE_UPLOADING_STATUS.FAILED
  ).length;
  const droppedCount = files.filter(
    (f) => f.status === FILE_UPLOADING_STATUS.DROPPED
  ).length;
  const totalProgress = files.length
    ? Math.round(files.reduce((s, f) => s + f.progress, 0) / files.length)
    : 0;
  return {
    totalProgress,
    successCount,
    failedCount,
    fileCount: files.length,
    droppedCount
  };
}

export function useUploadCoordinator() {
  const filesRef = useRef<UploadFile[]>([]);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [scanProgress, setScanProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);
  const [isSessionConflictOpen, setIsSessionConflictOpen] = useState(false);
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.auth.user);
  const dispatch = useDispatch();
  const {
    parentId,
    updateProgress,
    isPaused,
    resumeUpload,
    closeUpload,
    filesOverrideHandlerRef,
    setFilesOverrideHandler,
    isResumeUpload,
    registerPauseHandler,
    registerResumeHandler,
    registerRetryHandler,
    registerClearFilesHandler
  } = useUploadWidget();

  const {
    checkServerState,
    comparison: serverStateComparison,
    destinationPath: serverStateDestinationPath,
    isDialogOpen: isComparisonDialogOpen,
    isLoading: isServerStateLoading,
    overwrite: serverStateOverwrite,
    setOverwrite: setServerStateOverwrite,
    handleConfirm: handleComparisonConfirm,
    handleCancel: handleComparisonCancel,
    conflict: serverStateConflict,
    isConflictOpen: isConflictDialogOpen,
    handleConflictClose: handleConflictClose
  } = useServerStateCheck(parentId);

  const serverStateOverwriteRef = useRef(serverStateOverwrite);
  serverStateOverwriteRef.current = serverStateOverwrite;

  const [uploadDocuments] = useMutation({
    api: useUploadDocumentsMutation,
    errorContext: ErrorContext.FILES,
    subContext:
      (user?.accountType === ACCOUNT_TYPE.ORGANIZATION_ADMINISTRATOR ||
        user?.accountType === ACCOUNT_TYPE.ORGANIZATION_OWNER) &&
      ErrorContext.OA,
    fixedCacheKey: `uploadDocuments-${parentId}`
  });

  const [patchSessionStatus] = useMutation({
    api: usePatchUploadSessionStatusMutation,
    errorContext: ErrorContext.FILES
  });

  const session = useUploadSession(parentId);

  const successCount = useMemo(
    () =>
      files.filter((f) => f.status === FILE_UPLOADING_STATUS.UPLOADED).length,
    [files]
  );
  const failedCount = useMemo(
    () => files.filter((f) => f.status === FILE_UPLOADING_STATUS.FAILED).length,
    [files]
  );
  const droppedCount = useMemo(
    () =>
      files.filter((f) => f.status === FILE_UPLOADING_STATUS.DROPPED).length,
    [files]
  );

  const totalProgress = useMemo(() => {
    if (!files.length) {
      return 0;
    }
    return Math.round(
      files.reduce((sum, f) => sum + f.progress, 0) / files.length
    );
  }, [files]);

  const syncFiles = (next: UploadFile[]) => {
    filesRef.current = next;
    setFiles(next);
    updateProgress(computeProgress(next));
  };

  // Register a clear handler so the context can reset the file list whenever
  // a new resume override is set — even if isResumeUpload was already true
  // from a previous resume attempt (a state toggle wouldn't re-fire the effect).
  useEffect(() => {
    registerClearFilesHandler(() => {
      syncFiles([]);
      setScanProgress(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerClearFilesHandler]);

  const updateFileStatus = useCallback(
    (nativeFiles: File[], patch: Partial<UploadFile>) => {
      syncFiles(
        filesRef.current.map((uiFile) =>
          nativeFiles.includes(uiFile.file) ? { ...uiFile, ...patch } : uiFile
        )
      );
    },
    [syncFiles]
  );

  const handleFilesRejected = useCallback(
    (rejected: RejectedFile[]) => {
      syncFiles(
        filesRef.current.map((uiFile) => {
          const match = rejected.find((r) => r.file === uiFile.file);
          if (!match) {
            return uiFile;
          }
          return {
            ...uiFile,
            status: FILE_UPLOADING_STATUS.DROPPED,
            progress: 100,
            error:
              match.reason === FILE_REJECTION_REASON.OVERSIZED
                ? FILE_UPLOAD_ERROR_MESSAGES.FILE_TOO_LARGE
                : FILE_UPLOAD_ERROR_MESSAGES.UNSUPPORTED
          };
        })
      );
    },
    [syncFiles]
  );

  const {
    uploadGroupDocs,
    retryFailedFiles,
    abortAllActiveRequests
  } = useUploadDocuments({
    parentFolderId: parentId,
    extraFormDataArgs: session.getSessionData,
    uploadApiFn: (formData, abortContainer, getFormData) => {
      return new Promise((resolve, reject) => {
        uploadDocuments({
          params: {
            params: {
              formData: formData
            }
          },
          getParams: getFormData
            ? async () => ({ params: { formData: await getFormData() } })
            : undefined,
          abortContainer,
          successCallback: (response) => resolve(response),
          errorCallback: (error) => reject(error)
        });
      });
    },

    onBatchPreparing: (batch) =>
      updateFileStatus(batch, {
        status: FILE_UPLOADING_STATUS.PREPARING,
        progress: 5
      }),

    onUploadStart: (batch) =>
      updateFileStatus(batch, {
        status: FILE_UPLOADING_STATUS.UPLOADING,
        progress: 30
      }),

    onBatchSuccess: (batch) => {
      updateFileStatus(batch, {
        status: FILE_UPLOADING_STATUS.UPLOADED,
        progress: 100
      });
    },

    onBatchError: (batch, error) => {
      // Pausing aborts the in-flight request, which rejects asynchronously
      // and can land after handlePause's own synchronous QUEUED update -
      // without this check that race would flip the file back to FAILED,
      // taking it out of handleResume's "resend everything QUEUED" pass.
      const isAborted =
        error?.name === 'AbortError' ||
        (error?.status === 'FETCH_ERROR' && error?.error === 'Aborted');

      updateFileStatus(
        batch,
        isAborted
          ? { status: FILE_UPLOADING_STATUS.QUEUED, progress: 0 }
          : {
              status: FILE_UPLOADING_STATUS.FAILED,
              progress: 100,
              error: error?.data?.message || error?.message || 'Upload failed'
            }
      );
    },
    onUploadComplete: (successCount) => {
      if (successCount > 0) {
        dispatch(dataLibraryApi.util.invalidateTags(['Files']));
      }
    }
  });


  const handlePause = useCallback(() => {
    abortAllActiveRequests();
    const pending = filesRef.current.filter(
      (f) =>
        f.status === FILE_UPLOADING_STATUS.QUEUED ||
        f.status === FILE_UPLOADING_STATUS.PREPARING ||
        f.status === FILE_UPLOADING_STATUS.UPLOADING
    );
    syncFiles(
      filesRef.current.map((f) =>
        pending.includes(f)
          ? { ...f, status: FILE_UPLOADING_STATUS.QUEUED, progress: 0 }
          : f
      )
    );
  }, [abortAllActiveRequests, syncFiles]);

  const handleResume = useCallback(async () => {
    const queued = filesRef.current.filter(
      (f) => f.status === FILE_UPLOADING_STATUS.QUEUED
    );
    if (queued.length === 0) {
      return;
    }

    // These files already have a session_id registered server-side from the
    // original upload/addFiles call. Going through uploadGroupDocs here
    // would create a brand-new session for files the server already has
    // pending under the original session - a 400 Bad Request - and would
    // also re-run checkServerState's conflict dialog, which never resolves
    // while the widget is minimized (its UI isn't mounted), silently
    // stalling resume forever. Resend them directly against the existing
    // session instead, the same way "Retry Failed Items" does.
    const filesByType = queued.reduce((acc, f) => {
      const type = f.uploadType || UPLOAD_TYPE.FILE;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(f.file);
      return acc;
    }, {} as Record<string, File[]>);

    for (const [type, filesToUpload] of Object.entries(filesByType)) {
      // eslint-disable-next-line no-await-in-loop
      await retryFailedFiles(filesToUpload, type as UPLOAD_TYPE);
    }
  }, [retryFailedFiles]);

  // Lets the modal's own "Resume" button (shown while the session is
  // paused and files are still loaded) drive the same resend as the
  // Session History pages, without requiring the user to navigate there:
  // flip the session back to active server-side first - the upload
  // endpoint only accepts files while the session is active - then run the
  // normal resume handler.
  const resumePausedSession = useCallback(async () => {
    const sessionId = session.getSessionId();
    if (sessionId) {
      await patchSessionStatus({ params: { id: sessionId, status: 'active' } });
    }
    resumeUpload();
  }, [patchSessionStatus, resumeUpload]);

  const addFiles = useCallback(
    async (incomingFiles: File[], uploadType: UPLOAD_TYPE) => {
      const isResumeSelection = !!filesOverrideHandlerRef.current;
      const transactionId = uuid();

      const { valid: validFiles, rejected } = validateUploadFiles(
        incomingFiles,
        ALLOWED_DATA_LIBRARY_EXTENSIONS
      );
      const rejectedSet = new Set(rejected.map((r) => r.file));

      const mapped: UploadFile[] = [];

      setScanProgress({ processed: 0, total: incomingFiles.length });

      let index = 0;
      while (index < incomingFiles.length) {
        const sliceStartedAt = performance.now();
        while (
          index < incomingFiles.length &&
          performance.now() - sliceStartedAt < FRAME_BUDGET_MS
        ) {
          const file = incomingFiles[index];
          const isRejected = rejectedSet.has(file);
          mapped.push({
            id: uuid(),
            file,
            name: file.name,
            size: file.size,
            uploadType,
            status: isRejected
              ? FILE_UPLOADING_STATUS.DROPPED
              : FILE_UPLOADING_STATUS.IDLE,
            progress: isRejected ? 100 : 0
          });
          index += 1;
        }

        setScanProgress({ processed: index, total: incomingFiles.length });
        // eslint-disable-next-line no-await-in-loop
        await yieldToMain();
      }

      setScanProgress(null);
      syncFiles([...filesRef.current, ...mapped]);

      if (rejected.length > 0) {
        handleFilesRejected(rejected);
      }

      if (isResumeSelection) {
        // A caller (e.g. a paused Upload Session's "Resume" flow with no
        // files left in memory) asked to reuse this dialog's drag-and-drop
        // UI - just list the files so the user can review them. The
        // "Resume" button hands them off to the override instead of
        // running the normal validation/session-creation/upload pipeline.
        return;
      }

      const uploadableFiles = validFiles.filter((f) =>
        mapped.some(
          (m) => m.file === f && m.status === FILE_UPLOADING_STATUS.IDLE
        )
      );

      if (uploadableFiles.length > 0) {
        const selectedFiles = await checkServerState(uploadableFiles);

        if (selectedFiles && selectedFiles.length > 0) {
          session.resetSession();
          const { ok: sessionCreated, isConflict } = await session.createSession(
            selectedFiles,
            transactionId,
            uploadType,
            serverStateOverwriteRef.current
          );

          if (!sessionCreated) {
            if (isConflict) {
              setIsSessionConflictOpen(true);
            }
            updateFileStatus(selectedFiles, {
              status: FILE_UPLOADING_STATUS.FAILED,
              progress: 100,
              error: t(T.uploadFailedText)
            });
            return;
          }

          await uploadGroupDocs(
            selectedFiles,
            transactionId,
            uploadType,
          );
        }
      }
    },
    [
      syncFiles,
      uploadGroupDocs,
      checkServerState,
      filesOverrideHandlerRef,
      handleFilesRejected,
      updateFileStatus
    ]
  );

  // The modal's "Resume" button (shown once files are listed while a
  // resume override is pending) - hands the currently listed files to
  // whichever flow requested them (e.g. the diff/resync view) instead of
  // the normal upload pipeline, then closes the dialog.
  const confirmResumeSelection = useCallback(() => {
    const filesOverride = filesOverrideHandlerRef.current;
    if (!filesOverride) {
      return;
    }
    setFilesOverrideHandler(null);
    filesOverride(filesRef.current.map((f) => f.file));
    closeUpload();
  }, [filesOverrideHandlerRef, setFilesOverrideHandler, closeUpload]);

  const removeFile = useCallback(
    (id: string) => {
      syncFiles(filesRef.current.filter((f) => f.id !== id));
    },
    [syncFiles]
  );

  const retryFailedUploads = useCallback(async () => {
    const failedUiFiles = filesRef.current.filter(
      (f) =>
        f.status === FILE_UPLOADING_STATUS.FAILED &&
        !f.error?.includes('Unsupported')
    );

    if (failedUiFiles.length === 0) {
      return;
    }

    const sessionId = session.getSessionId();
    if (sessionId) {
      const patched = await patchSessionStatus({ params: { id: sessionId, status: 'active' } });
      if (patched === undefined) return;
    }

    syncFiles(
      filesRef.current.map((f) =>
        failedUiFiles.includes(f)
          ? { ...f, status: FILE_UPLOADING_STATUS.UPLOADING, progress: 30 }
          : f
      )
    );

    const filesByType = failedUiFiles.reduce((acc, f) => {
      const type = f.uploadType || UPLOAD_TYPE.FILE;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(f.file);
      return acc;
    }, {} as Record<string, File[]>);

    const hasActiveSession = !!session.getSessionId();

    for (const [type, filesToUpload] of Object.entries(filesByType)) {
      if (hasActiveSession) {
        // eslint-disable-next-line no-await-in-loop
        await retryFailedFiles(filesToUpload, type as UPLOAD_TYPE);
      } else {
        // A session was never created for these files (e.g. the destination was
        // locked and createUploadSession itself failed) - resending them via
        // retryFailedFiles would skip session creation entirely and upload them
        // session-less, bypassing whatever check failed the first time. Route
        // back through uploadGroupDocs instead so the destination (lock, etc.)
        // is re-validated on every retry, not just the first attempt.
        // eslint-disable-next-line no-await-in-loop
        await uploadGroupDocs(filesToUpload, uuid(), type as UPLOAD_TYPE);
      }
    }
  }, [syncFiles, retryFailedFiles, uploadGroupDocs, patchSessionStatus]);

  useEffect(() => {
    registerPauseHandler(handlePause);
    registerResumeHandler(handleResume);
    registerRetryHandler(retryFailedUploads);
  }, [
    handlePause,
    handleResume,
    retryFailedUploads,
    registerPauseHandler,
    registerResumeHandler,
    registerRetryHandler
  ]);

  const handleCancelAll = useCallback(() => {
    abortAllActiveRequests();
    if (successCount > 0) {
      dispatch(dataLibraryApi.util.invalidateTags(['Files']));
    }
    syncFiles([]);
  }, [abortAllActiveRequests, successCount, syncFiles]);

  return {
    files,
    addFiles,
    removeFile,
    totalProgress,
    successCount,
    failedCount,
    droppedCount,
    retryFailedUploads,
    handleCancelAll,
    isPaused,
    resumePausedSession,
    isResumeUpload,
    confirmResumeSelection,
    scanProgress,
    serverStateComparison,
    serverStateDestinationPath,
    isComparisonDialogOpen,
    isServerStateLoading,
    serverStateOverwrite,
    setServerStateOverwrite,
    handleComparisonConfirm,
    handleComparisonCancel,
    serverStateConflict,
    isConflictDialogOpen,
    handleConflictClose,
    isSessionConflictOpen,
    setIsSessionConflictOpen
  };
}